/**
 * Interprétation du résultat d'une tâche de planification.
 *
 * Le backend renvoie soit l'objet planifié, soit un code d'erreur sous forme de
 * chaîne (`error_teacher_not_available`, `error_slot_already_booked`, ...), soit
 * `false` quand la tâche n'a rien produit. Un HTTP 200 ne signifie donc pas que
 * la réservation a eu lieu : sans ce contrôle, l'élève voit « cours réservé »
 * alors que rien n'a été créé.
 *
 * Les codes vivent côté backend dans plan/algorithm/ ; cette table sert de point
 * unique de traduction, pour que l'ajout d'un code ne se traduise plus par une
 * comparaison de chaînes recopiée dans chaque composant.
 */

/**
 * Libellés des échecs de réservation de cours. Les codes propres au booking
 * LILATE gardent leur formulation dans les composants concernés, qui parlent de
 * « passage » et non de « cours ».
 */
const PLAN_ERROR_MESSAGES: { [code: string]: string } = {
  error_teacher_not_available:
    'Le cours n\'a pas pu être réservé ! Merci de contacter le service client.',
  error_slot_already_booked:
    'Ce créneau vient d\'être réservé. Merci d\'en choisir un autre.',
};

const GENERIC_PLAN_ERROR =
  'Le cours n\'a pas pu être réservé ! Merci de contacter le service client.';

/**
 * Extrait le code d'erreur d'un résultat de planification.
 *
 * Accepte les deux formes renvoyées par le backend : la chaîne brute des
 * algorithmes de cours, et l'objet `{status, msg, data}` du booking LILATE.
 *
 * @returns le code d'erreur, ou null si la tâche a réussi.
 */
export function getPlanErrorCode(result: any): string | null {
  if (result === false || result === null || result === undefined) {
    return 'error_plan_failed';
  }
  if (typeof result === 'string') {
    return result.indexOf('error') === 0 ? result : null;
  }
  if (typeof result === 'object' && result.status === 'error') {
    return result.msg || 'error_plan_failed';
  }
  return null;
}

/** La tâche de planification a-t-elle échoué ? */
export function isPlanError(result: any): boolean {
  return getPlanErrorCode(result) !== null;
}

/**
 * Message destiné à l'élève pour un résultat de planification en échec.
 * Retourne null quand la planification a réussi.
 */
export function getPlanErrorMessage(result: any): string | null {
  const code = getPlanErrorCode(result);
  if (!code) {
    return null;
  }
  return PLAN_ERROR_MESSAGES[code] || GENERIC_PLAN_ERROR;
}
